/**
 * Calendar Widget Frontend JavaScript (ES Module)
 * Integrates FullCalendar with ical-expander for ICS parsing
 *
 * @package CalendarWidget
 */

// Local imports (bundled via esbuild)
import { Calendar } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import IcalExpander from "ical-expander";
import { createIcons, Calendar as LucideCalendar, CalendarRange, Clock, MapPin, Users, Link as LucideLink } from "lucide";

const lucideIcons = { Calendar: LucideCalendar, CalendarRange, Clock, MapPin, Users, Link: LucideLink };

// --- Config ---
const CALENDAR_CONFIG = {
    lookbackYears: 2,
    lookaheadYears: 2,
    maxIterations: 1000,
    defaultColor: '#667eea',
};

// --- Per-calendar state ---
const STATE = new WeakMap();

function getState(containerEl) {
    let state = STATE.get(containerEl);
    if (!state) {
        state = {
            eventCache: [],
            selectedDate: null,
            loadedFeedUrls: new Set(),
            fetchIcsCache: new Map(),
        };
        STATE.set(containerEl, state);
    }
    return state;
}

// --- Date helpers ---
const toLocalMidnight = (dateInput) => {
    const date = new Date(dateInput);
    date.setHours(0, 0, 0, 0);
    return date;
};

const toLocalDateString = (dateInput) => {
    const date = toLocalMidnight(dateInput);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// --- WP AJAX helper ---
const wpPost = async (params) => {
    const res = await fetch(calendarWidgetData.ajaxUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ nonce: calendarWidgetData.nonce, ...params }),
    });
    const json = await res.json();
    if (!json?.success) throw new Error(params.action + ' failed');
    return json.data;
};

// --- Fetch ICS via WP proxy (with de-dupe & caching) ---
async function fetchIcsViaWp(containerEl, url) {
    const state = getState(containerEl);
    const key = url.trim().toLowerCase();
    if (!key) throw new Error('Empty feed url');

    if (state.fetchIcsCache.has(key)) return state.fetchIcsCache.get(key);

    const p = wpPost({
        action: 'calendar_widget_fetch_ics',
        url,
    }).then((data) => data.ics_content);

    state.fetchIcsCache.set(key, p);
    return p;
}

// --- ICS Helpers ---
const IcsHelper = {
    getProp: (comp, name) => {
        try { return comp?.getFirstPropertyValue(name) || null; } catch (e) { return null; }
    },
    getMulti: (comp, name) => {
        try { return comp ? comp.getAllProperties(name).flatMap(p => p.getValues()) : []; } catch (e) { return []; }
    },
    cleanEmail: (val) => (val?.startsWith('mailto:') ? val.slice(7) : val),
    parseAttendee: (att) => {
        try {
            return {
                email: IcsHelper.cleanEmail(att.getFirstValue()),
                status: att.getParameter('partstat'),
                role: att.getParameter('role'),
                name: att.getParameter('cn') || 'Unknown',
            };
        } catch (e) {
            return { email: '', status: '', role: '', name: 'Unknown' };
        }
    },
};

const buildFullCalendarEvent = (event) => {
    const item = event.item || event;
    const comp = item.component;
    const parser = new EventMarkdownParser(item.description);
    // Strip join/meeting links when explicitly marked Private: [true] or Public: [false].
    // Unset Public field is treated as public (links visible by default).
    const shouldStrip = parser.isPrivate() || parser.getMetadata('Public') === false;
    const description = shouldStrip ? stripPrivateMetadata(item.description) : item.description;
    return {
        title: item.summary,
        start: event.startDate.toJSDate(),
        end: event.endDate.toJSDate(),
        allDay: event.startDate.isDate,
        extendedProps: {
            uid: item.uid,
            description,
            location: item.location,
            status: IcsHelper.getProp(comp, 'status'),
            url: IcsHelper.getProp(comp, 'url'),
            categories: IcsHelper.getMulti(comp, 'categories'),
            attendees: comp?.getAllProperties('attendee')?.map(IcsHelper.parseAttendee) || [],
        },
    };
};

function parseIcsToEvents(icsData) {
    const now = new Date();
    const start = new Date(now.getFullYear() - CALENDAR_CONFIG.lookbackYears, now.getMonth(), now.getDate());
    const end = new Date(now.getFullYear() + CALENDAR_CONFIG.lookaheadYears, now.getMonth(), now.getDate());

    const expander = new IcalExpander({ ics: icsData, maxIterations: CALENDAR_CONFIG.maxIterations });
    const { events, occurrences } = expander.between(start, end);
    return [...events, ...occurrences].map(buildFullCalendarEvent);
}

// --- Load feeds from WP (parallel fetch + dedupe) ---
async function loadIcsFeeds(containerEl, calendar) {
    const shortcodeId = containerEl.getAttribute('data-shortcode-id') || '';
    const data = await wpPost({
        action: 'calendar_widget_get_ics_urls',
        shortcode_id: shortcodeId,
    });

    const feeds = data?.feeds || [];
    if (!feeds.length) return;

    const state = getState(containerEl);

    const uniq = [];
    for (const f of feeds) {
        const url = (f.url || '').trim();
        if (!url) continue;
        const key = url.toLowerCase();
        if (state.loadedFeedUrls.has(key)) continue;
        state.loadedFeedUrls.add(key);
        uniq.push({ url, color: f.color || CALENDAR_CONFIG.defaultColor });
    }

    if (!uniq.length) return;

    const results = await Promise.allSettled(
        uniq.map(async (f) => {
            const icsText = await fetchIcsViaWp(containerEl, f.url);
            const events = parseIcsToEvents(icsText).map((e) => ({
                ...e,
                backgroundColor: f.color,
                borderColor: f.color,
                textColor: '#fff',
            }));
            return events;
        })
    );

    const all = [];
    for (const r of results) {
        if (r.status === 'fulfilled') all.push(...r.value);
        else console.warn('Feed failed:', r.reason);
    }

    if (all.length) {
        state.eventCache.push(...all);
        calendar.addEventSource(all);
        if (state.selectedDate) {
            renderEventList(containerEl, state);
        }
    }
}

// --- Markdown Parser for Event Descriptions ---
class EventMarkdownParser {
    constructor(markdown) {
        this.markdown = markdown || '';
        this.parsed = {
            title: '',
            metadata: {},
            sections: {},
            footerText: '', // Text between last --- markers
            rawMarkdown: this.markdown
        };
        this.parse();
    }

    parse() {
        if (!this.markdown) return;

        // Split by --- to find sections and footer
        const parts = this.markdown.split(/^-{3,}$/m);

        // Process each part
        let inColumnSection = false;
        let currentSection = null;
        let sectionContent = [];
        let footerTexts = [];

        for (let partIndex = 0; partIndex < parts.length; partIndex++) {
            const part = parts[partIndex].trim();
            if (!part) continue;

            const lines = part.split('\n');
            const hasHeading = lines.some(l => l.trim().startsWith('## '));

            // If this part has ## headings, it's a column section
            if (hasHeading) {
                inColumnSection = true;
                for (const line of lines) {
                    const trimmed = line.trim();
                    if (!trimmed) continue;

                    if (trimmed.startsWith('## ')) {
                        if (currentSection) {
                            this.parsed.sections[currentSection] = this.parseSection(sectionContent);
                        }
                        currentSection = trimmed.replace(/^## /, '').trim();
                        sectionContent = [];
                        continue;
                    }
                    if (currentSection) sectionContent.push(trimmed);
                }
            } else if (inColumnSection && partIndex > 0) {
                // Text after column sections (between --- markers) - collect as footer
                footerTexts.push(part);
            } else {
                // Parse metadata and title from non-column parts
                for (const line of lines) {
                    const trimmed = line.trim();
                    if (!trimmed) continue;

                    if (trimmed.startsWith('# ')) {
                        this.parsed.title = this.extractTitle(trimmed);
                        continue;
                    }
                    if (trimmed.match(/^\*\*[^*]+:\*\*/)) {
                        const metadata = this.parseMetadata(trimmed);
                        if (metadata) this.parsed.metadata[metadata.key] = metadata.value;
                        continue;
                    }
                }
            }
        }

        // Save last section if any
        if (currentSection) {
            this.parsed.sections[currentSection] = this.parseSection(sectionContent);
        }

        // Only keep the last footer text (text between --- markers)
        if (footerTexts.length > 0) {
            this.parsed.footerText = footerTexts[footerTexts.length - 1];
        }
    }

    extractTitle(line) {
        let title = line.replace(/^# /, '').trim();
        const linkMatch = title.match(/\[([^\]]+)\]/);
        return linkMatch ? linkMatch[1] : title;
    }

    parseMetadata(line) {
        const match = line.match(/^\*\*([^*]+):\*\*\s*(.+)$/);
        if (!match) return null;
        let key = match[1].trim();
        let value = match[2].trim();

        // Handle [true] or [false] format
        const bracketMatch = value.match(/^\[?(true|false)\]?$/i);
        if (bracketMatch) {
            return { key, value: bracketMatch[1].toLowerCase() === 'true' };
        }

        // Handle [text](url) markdown link format
        const linkMatch = value.match(/\[([^\]]+)\]\(([^)]+)\)/);
        if (linkMatch) {
            return { key, value: { text: linkMatch[1], url: linkMatch[2], isLink: true } };
        }

        // Handle [url] bracket-only format (e.g. [https://example.com])
        const bracketUrlMatch = value.match(/^\[([^\]]+)\]$/);
        if (bracketUrlMatch && bracketUrlMatch[1].match(/^https?:\/\//)) {
            const url = bracketUrlMatch[1];
            return { key, value: { text: url, url, isLink: true } };
        }

        if (value.toLowerCase() === 'true' || value.toLowerCase() === 'false') {
            value = value.toLowerCase() === 'true';
        }
        return { key, value };
    }

    parseSection(lines) {
        const items = [];
        let currentItem = '';
        for (const line of lines) {
            if (line.match(/^\d+\.\s/) || line.match(/^[\*\-]\s/)) {
                if (currentItem) items.push(currentItem.trim());
                currentItem = line.replace(/^\d+\.\s/, '').replace(/^[\*\-]\s/, '');
            } else if (currentItem) {
                currentItem += ' ' + line;
            } else {
                currentItem = line;
            }
        }
        if (currentItem) items.push(currentItem.trim());
        return items.map(item => this.parseLinks(item));
    }

    parseLinks(text) {
        const linkMatch = text.match(/\[([^\]]+)\]\(([^)]+)\)/);
        if (linkMatch) {
            return { text: linkMatch[1], url: linkMatch[2], fullText: text, isLink: true };
        }
        return { text, fullText: text, isLink: false };
    }

    getData() { return this.parsed; }
    getTitle() { return this.parsed.title; }
    getMetadata(key) { return this.parsed.metadata[key]; }
    getSection(name) { return this.parsed.sections[name]; }
    getFooterText() { return this.parsed.footerText; }
    isPrivate() {
        const v = this.getMetadata('Private');
        return v === true;
    }
    // Default to false if Public is not set (Private overrides Public)
    isPublic() {
        if (this.isPrivate()) return false;
        const v = this.getMetadata('Public');
        return v === true;
    }
    getMeetingUrl() { const v = this.getMetadata('Meeting URL'); return v?.isLink ? v.url : v; }
}

// --- Description formatting ---
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

function formatDescription(raw) {
    if (!raw) return '';
    let html = escapeHtml(raw);
    // Headings: # , ##
    html = html.replace(/^### (.+)$/gm, '<h5>$1</h5>');
    html = html.replace(/^## (.+)$/gm, '<h4>$1</h4>');
    html = html.replace(/^# (.+)$/gm, '<h3>$1</h3>');
    // Bold / italic
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    // Links [text](url)
    html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener" class="text-blue-500 hover:text-blue-600 underline">$1</a>');
    // Horizontal rules
    html = html.replace(/^-{3,}$/gm, '<hr class="my-3 border-gray-200">');
    // Unordered list items
    html = html.replace(/^[\*\-] (.+)$/gm, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>\n?)+/g, (match) => `<ul class="list-disc pl-5 my-2">${match}</ul>`);
    // Ordered list items
    html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');
    // Newlines to <br> (but not inside block elements)
    html = html.replace(/\n/g, '<br>');
    // Clean up <br> right after block elements
    html = html.replace(/(<\/h[345]>)<br>/g, '$1');
    html = html.replace(/(<\/li>)<br>/g, '$1');
    html = html.replace(/(<hr[^>]*>)<br>/g, '$1');
    html = html.replace(/(<\/ul>)<br>/g, '$1');
    return html;
}

function formatLocationValue(raw) {
    if (!raw) return '';
    const escaped = escapeHtml(String(raw));
    return linkifyPlainUrls(escaped);
}

function linkifyPlainUrls(text) {
    return text.replace(/(^|[\s(>])((https?:\/\/)[^\s<]+)/g, (match, prefix, url) => {
        const trimmedUrl = url.replace(/[),.;:!?]+$/g, '');
        const trailing = url.slice(trimmedUrl.length);
        return `${prefix}<a href="${trimmedUrl}" target="_blank" rel="noopener" class="text-blue-500 hover:text-blue-600 underline">${trimmedUrl}</a>${trailing}`;
    });
}

function getGroupPageUrl(raw) {
    if (!raw) return null;
    const match = String(raw).match(/(?:^|\n)\s*GROUP PAGE\s*:\s*(https?:\/\/\S+)/i);
    if (!match) return null;
    return match[1].replace(/[),.;:!?]+$/g, '');
}

function getHowToJoinUrl(raw) {
    if (!raw) return null;
    const match = String(raw).match(/(?:^|\n)\s*HOW TO JOIN\s*:\s*(https?:\/\/\S+)/i);
    if (!match) return null;
    return match[1].replace(/[),.;:!?]+$/g, '');
}

// Strip join/meeting links from description when Public: [false] or Private: [true]
function stripPrivateMetadata(description) {
    if (!description) return description;
    return description
        .replace(/^\*\*(Meeting URL|How to join):\*\*.*$/gim, '')
        .replace(/^HOW TO JOIN\s*:.*$/gim, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

// --- UI Helpers ---
function createElement(tag, className, textContent) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (textContent != null) el.textContent = textContent;
    return el;
}

function formatTimeRange(event) {
    if (event.allDay) return 'All day';
    const timeOptions = { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' };
    const startTime = event.start.toLocaleTimeString([], timeOptions);
    if (!event.end || event.end <= event.start) return startTime;
    const endTime = event.end.toLocaleTimeString([], timeOptions);
    return `${startTime} - ${endTime}`;
}

function getDailyEvents(state) {
    if (!state.selectedDate) return [];
    const nextDay = new Date(state.selectedDate);
    nextDay.setDate(nextDay.getDate() + 1);
    return state.eventCache.filter((e) => e.start < nextDay && e.end > state.selectedDate);
}

function getMonthlyEvents(state, date) {
    const year = date.getFullYear();
    const month = date.getMonth();
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0, 23, 59, 59, 999);
    return state.eventCache.filter((e) => {
        const eventStart = new Date(e.start);
        return eventStart >= monthStart && eventStart <= monthEnd;
    });
}

// --- Event List Rendering ---
function renderEventList(containerEl, state) {
    const eventListContainer = containerEl.querySelector('.jscal-event-list-container');
    if (!eventListContainer) return;

    const eventListSection = containerEl.querySelector('.jscal-event-list');
    const eventListHeader = eventListSection ? eventListSection.querySelector('h3') : null;

    let eventsToShow = [];

    if (state.selectedDate) {
        eventsToShow = getDailyEvents(state);
        if (eventListHeader) {
            eventListHeader.textContent = `Events - ${state.selectedDate.toLocaleDateString('en-US', {
                weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC'
            })}`;
        }
    } else {
        const calendarGridEl = containerEl.querySelector('.jscal-calendar-grid');
        if (calendarGridEl && calendarGridEl.__fcInstance) {
            eventsToShow = getMonthlyEvents(state, calendarGridEl.__fcInstance.getDate());
        } else {
            eventsToShow = getMonthlyEvents(state, new Date());
        }
        if (eventListHeader) {
            eventListHeader.textContent = 'All Events';
        }
    }

    if (eventsToShow.length === 0) {
        eventListContainer.innerHTML = `<div class="no-events">${calendarWidgetData?.strings?.noEvents || 'No events found'}</div>`;
        return;
    }

    eventsToShow.sort((a, b) => new Date(a.start) - new Date(b.start));
    eventListContainer.innerHTML = '';

    eventsToShow.forEach(event => {
        const eventItem = createEventListItem(event, containerEl);
        eventListContainer.appendChild(eventItem);
    });

    createIcons({ icons: lucideIcons });
}

function createEventListItem(event, containerEl) {
    const eventItem = document.createElement('div');
    eventItem.className = 'p-8 mb-5 rounded-xl border border-gray-200';

    const extendedProps = event.extendedProps || {};
    const startDate = new Date(event.start);
    const now = new Date();

    // Parse markdown description
    const parser = new EventMarkdownParser(extendedProps.description);
    const parsedData = parser.getData();

    // 1. Title (big, bold)
    const eventTitle = createElement('div', 'text-3xl font-bold text-slate-800 mb-4 leading-tight', event.title || 'Untitled Event');
    eventItem.appendChild(eventTitle);

    // 2. Status badge (Upcoming/Past)
    const isUpcoming = startDate > now;
    const statusBadge = createElement('div', 'inline-block px-4 py-2 rounded-full text-sm font-semibold mb-6');
    statusBadge.textContent = isUpcoming ? 'Upcoming' : 'Past';
    statusBadge.style.backgroundColor = isUpcoming ? '#0439E3' : '#9CA3AF';
    statusBadge.style.color = '#FFFFFF';
    eventItem.appendChild(statusBadge);

    // 3. First meta row: Recurrence + Attendees
    const metaRow1 = createElement('div', 'flex items-center gap-6 flex-wrap mb-4 text-base text-slate-800');

    // Recurrence info (if available)
    const recurrenceText = getRecurrenceText(extendedProps.description, startDate);
    if (recurrenceText) {
        const recurrenceItem = createElement('div', 'flex items-center gap-2');
        recurrenceItem.innerHTML = `<i data-lucide="calendar-range" class="text-blue-500 w-5 h-5 flex-shrink-0"></i> ${escapeHtml(recurrenceText)}`;
        metaRow1.appendChild(recurrenceItem);
    }

    if (metaRow1.children.length > 0) {
        eventItem.appendChild(metaRow1);
    }

    // 4. Second meta row: Date | Time | Location
    const metaRow2 = createElement('div', 'flex items-center gap-2 flex-wrap mb-6 text-base text-slate-800');

    // Date
    const dateItem = createElement('div', 'flex items-center gap-2');
    dateItem.innerHTML = `<i data-lucide="calendar" class="text-blue-500 w-5 h-5 flex-shrink-0"></i> ${startDate.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })}`;
    metaRow2.appendChild(dateItem);

    const showLocation = extendedProps.location;

    // Separator
    if (!event.allDay || showLocation) {
        metaRow2.appendChild(createElement('span', 'text-gray-300 mx-2', '|'));
    }

    // Time (if not all-day)
    if (!event.allDay) {
        const timeItem = createElement('div', 'flex items-center gap-2');
        const timeStr = startDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' }) + ' UTC';
        timeItem.innerHTML = `<i data-lucide="clock" class="text-blue-500 w-5 h-5 flex-shrink-0"></i> ${timeStr}`;
        metaRow2.appendChild(timeItem);

        if (showLocation) {
            metaRow2.appendChild(createElement('span', 'text-gray-300 mx-2', '|'));
        }
    }

    // Location (only show when Public is true)
    if (showLocation) {
        const locationItem = createElement('div', 'flex items-center gap-2');
        const locationDisplay = formatLocationValue(extendedProps.location);
        locationItem.innerHTML = `<i data-lucide="map-pin" class="text-blue-500 w-5 h-5 flex-shrink-0"></i> ${locationDisplay}`;
        metaRow2.appendChild(locationItem);
    }

    // Organisers (from description metadata) - inline with date/time/location
    const organisers = parser.getMetadata('Organisers');
    if (organisers) {
        let organisersText = organisers.isLink ? organisers.text : (typeof organisers === 'string' ? organisers : '');
        // Remove brackets if present
        organisersText = organisersText.replace(/^\[|\]$/g, '');
        if (organisersText) {
            metaRow2.appendChild(createElement('span', 'text-gray-300 mx-2', '|'));
            const organisersItem = createElement('div', 'flex items-center gap-2');
            organisersItem.innerHTML = `<i data-lucide="users" class="text-blue-500 w-5 h-5 flex-shrink-0"></i> ${escapeHtml(organisersText)}`;
            metaRow2.appendChild(organisersItem);
        }
    }

    // Meeting URL (from description metadata)
    const metaMeetingUrl = parser.getMetadata('Meeting URL');
    if (metaMeetingUrl) {
        const meetingUrlValue = metaMeetingUrl.isLink ? metaMeetingUrl.url : (typeof metaMeetingUrl === 'string' ? metaMeetingUrl : '');
        if (meetingUrlValue) {
            metaRow2.appendChild(createElement('span', 'text-gray-300 mx-2', '|'));
            const meetingItem = createElement('div', 'flex items-center gap-2');
            meetingItem.innerHTML = `<i data-lucide="link" class="text-blue-500 w-5 h-5 flex-shrink-0"></i> <a href="${escapeHtml(meetingUrlValue)}" target="_blank" rel="noopener" class="text-blue-500 hover:text-blue-600 underline">Meeting Link</a>`;
            metaRow2.appendChild(meetingItem);
        }
    }

    eventItem.appendChild(metaRow2);

    // 5. Content: Render all ## sections as columns
    const sectionNames = Object.keys(parsedData.sections);
    const hasStructuredSections = sectionNames.length > 0;

    if (hasStructuredSections) {
        const contentGrid = createElement('div', 'grid grid-cols-1 md:grid-cols-2 gap-8 mb-6');

        for (const sectionName of sectionNames) {
            const sectionItems = parsedData.sections[sectionName];
            if (!sectionItems || sectionItems.length === 0) continue;

            const sectionDiv = document.createElement('div');
            sectionDiv.appendChild(createElement('h4', 'text-base font-bold text-slate-800 mb-3', `${sectionName}:`));
            const itemList = createElement('ul', 'list-none p-0 m-0');

            sectionItems.forEach(item => {
                const li = document.createElement('li');
                li.className = 'flex items-start gap-2 mb-2 text-slate-800 text-base leading-relaxed';
                if (item.isLink) {
                    li.innerHTML = `<span class="text-blue-500 mt-1">•</span> <a href="${escapeHtml(item.url)}" target="_blank" rel="noopener" class="text-blue-500 hover:text-blue-600 underline">${escapeHtml(item.text)}</a>`;
                } else {
                    li.innerHTML = `<span class="text-blue-500 mt-1">•</span> <span>${escapeHtml(item.text)}</span>`;
                }
                itemList.appendChild(li);
            });

            sectionDiv.appendChild(itemList);
            contentGrid.appendChild(sectionDiv);
        }

        eventItem.appendChild(contentGrid);
    }

    // 6. Footer text (last text between --- markers)
    const footerText = parser.getFooterText();
    if (footerText) {
        const footerContainer = createElement('div', 'mb-6');
        const footer = createElement('div', 'text-slate-600 text-base leading-relaxed desc-clamp');
        footer.innerHTML = formatDescription(footerText);
        footerContainer.appendChild(footer);

        // Add show more/less button
        const toggleBtn = createElement('button', 'text-blue-500 hover:text-blue-600 text-sm font-medium mt-2 cursor-pointer');
        toggleBtn.textContent = 'Show more';
        toggleBtn.style.display = 'none'; // Hidden by default, shown via JS check
        toggleBtn.addEventListener('click', () => {
            footer.classList.toggle('desc-expanded');
            toggleBtn.textContent = footer.classList.contains('desc-expanded') ? 'Show less' : 'Show more';
        });
        footerContainer.appendChild(toggleBtn);

        // Check if content exceeds 3 lines and show toggle button
        requestAnimationFrame(() => {
            if (footer.scrollHeight > footer.clientHeight) {
                toggleBtn.style.display = 'block';
            }
        });

        eventItem.appendChild(footerContainer);
    }

    // 7. Fallback: If no structured content, show raw description
    const hasAnyContent = hasStructuredSections || footerText;
    if (!hasAnyContent && extendedProps.description) {
        const descContainer = createElement('div', 'mb-6');
        const descriptionDiv = createElement('div', 'text-slate-600 text-base leading-relaxed desc-clamp jscal-description-content');
        descriptionDiv.innerHTML = formatDescription(extendedProps.description);
        descContainer.appendChild(descriptionDiv);

        // Add show more/less button
        const toggleBtn = createElement('button', 'text-blue-500 hover:text-blue-600 text-sm font-medium mt-2 cursor-pointer');
        toggleBtn.textContent = 'Show more';
        toggleBtn.style.display = 'none'; // Hidden by default, shown via JS check
        toggleBtn.addEventListener('click', () => {
            descriptionDiv.classList.toggle('desc-expanded');
            toggleBtn.textContent = descriptionDiv.classList.contains('desc-expanded') ? 'Show less' : 'Show more';
        });
        descContainer.appendChild(toggleBtn);

        // Check if content exceeds 3 lines and show toggle button
        requestAnimationFrame(() => {
            if (descriptionDiv.scrollHeight > descriptionDiv.clientHeight) {
                toggleBtn.style.display = 'block';
            }
        });

        eventItem.appendChild(descContainer);
    }

    // 8. Action buttons: "How to join" and "Go to group"
    const howToJoinUrl = parser.getMetadata('How to join');
    const groupPageUrl = parser.getMetadata('Group page');
    const meetingUrlMeta = parser.getMetadata('Meeting URL');
    const howToJoinLink = howToJoinUrl?.isLink ? howToJoinUrl.url : (typeof howToJoinUrl === 'string' ? howToJoinUrl : null);
    const groupPageLink = groupPageUrl?.isLink ? groupPageUrl.url : (typeof groupPageUrl === 'string' ? groupPageUrl : null);
    const meetingLink = meetingUrlMeta?.isLink ? meetingUrlMeta.url : (typeof meetingUrlMeta === 'string' ? meetingUrlMeta : null);

    if (howToJoinLink || groupPageLink || meetingLink) {
        const actions = createElement('div', 'flex gap-3 mt-6 flex-wrap');

        if (howToJoinLink) {
            const joinBtn = document.createElement('a');
            joinBtn.className = 'bg-green-500 text-white hover:bg-green-600 px-6 py-3 rounded-full font-semibold transition-all hover:-translate-y-0.5 hover:shadow-md inline-block';
            joinBtn.textContent = 'How to join';
            joinBtn.href = howToJoinLink;
            joinBtn.target = '_blank';
            joinBtn.rel = 'noopener';
            actions.appendChild(joinBtn);
        }

        if (meetingLink && !howToJoinLink) {
            const meetingBtn = document.createElement('a');
            meetingBtn.className = 'bg-green-500 text-white hover:bg-green-600 px-6 py-3 rounded-full font-semibold transition-all hover:-translate-y-0.5 hover:shadow-md inline-block';
            meetingBtn.textContent = 'Join Meeting';
            meetingBtn.href = meetingLink;
            meetingBtn.target = '_blank';
            meetingBtn.rel = 'noopener';
            actions.appendChild(meetingBtn);
        }

        if (groupPageLink) {
            const groupBtn = document.createElement('a');
            groupBtn.className = 'border-2 border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white px-6 py-3 rounded-full font-semibold transition-all hover:-translate-y-0.5 hover:shadow-md inline-block';
            groupBtn.textContent = 'Go to group';
            groupBtn.href = groupPageLink;
            groupBtn.target = '_blank';
            groupBtn.rel = 'noopener';
            actions.appendChild(groupBtn);
        }

        eventItem.appendChild(actions);
    }

    return eventItem;
}

// Helper to get recurrence text from description or generate from day
function getRecurrenceText(description, startDate) {
    if (!description) return null;
    // Try to find recurrence pattern in description
    const match = description.match(/(?:^|\n)\s*\*\*Recurrence:\*\*\s*(.+)/i);
    if (match) return match[1].trim();
    // Default: use day of week if it looks like a recurring event
    return null;
}

// --- Event Popup ---
function showEventPopup(event) {
    const title = event.title || 'Event Details';
    const startDate = event.start ? new Date(event.start) : null;
    const endDate = event.end ? new Date(event.end) : null;
    const extendedProps = event.extendedProps || {};
    const overlay = createElement('div', 'jscal-event-popup-overlay');
    const popup = createElement('div', 'event-popup');

    const header = createElement('div', 'event-popup-header');
    header.innerHTML = `<h3>${title}</h3><button class="popup-close">&times;</button>`;
    popup.appendChild(header);

    const body = createElement('div', 'event-popup-body');

    if (startDate) {
        body.innerHTML += `<div class="popup-detail"><span class="popup-label">Start:</span><span class="popup-value">${startDate.toLocaleString()}</span></div>`;
    }
    if (endDate) {
        body.innerHTML += `<div class="popup-detail"><span class="popup-label">End:</span><span class="popup-value">${endDate.toLocaleString()}</span></div>`;
    }
    if (extendedProps.location) {
        body.innerHTML += `<div class="popup-detail"><span class="popup-label">Location:</span><span class="popup-value">${extendedProps.location}</span></div>`;
    }
    if (extendedProps.description) {
        body.innerHTML += `<div class="popup-detail"><span class="popup-label">Description:</span><div class="popup-value">${formatDescription(extendedProps.description)}</div></div>`;
    }

    {
        const actionsDiv = createElement('div', 'flex gap-3 mt-6 pt-6 border-t border-gray-200');
        const meetingUrl = getHowToJoinUrl(extendedProps.description);
        const groupUrl = getGroupPageUrl(extendedProps.description);

        if (meetingUrl) {
            const joinBtn = document.createElement('a');
            joinBtn.className = 'bg-green-500 text-white hover:bg-green-600 px-6 py-3 rounded-full font-semibold transition-all hover:-translate-y-0.5 hover:shadow-md inline-block';
            joinBtn.textContent = 'How to join';
            joinBtn.href = meetingUrl;
            joinBtn.target = '_blank';
            actionsDiv.appendChild(joinBtn);
        }

        if (groupUrl) {
            const groupBtn = document.createElement('a');
            groupBtn.className = 'border-2 border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white px-6 py-3 rounded-full font-semibold transition-all hover:-translate-y-0.5 hover:shadow-md inline-block';
            groupBtn.textContent = 'Go to group';
            groupBtn.href = groupUrl;
            groupBtn.target = '_blank';
            actionsDiv.appendChild(groupBtn);
        }

        if (actionsDiv.children.length > 0) {
            body.appendChild(actionsDiv);
        }
    }
    popup.appendChild(body);
    overlay.appendChild(popup);
    document.body.appendChild(overlay);

    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) document.body.removeChild(overlay);
    });
    header.querySelector('.popup-close').addEventListener('click', () => {
        document.body.removeChild(overlay);
    });
}

// --- Date select handler ---
function handleDateSelect(info, calendar, containerEl) {
    const state = getState(containerEl);

    containerEl.querySelectorAll('.fc-day.selected-day').forEach(x => x.classList.remove('selected-day'));
    info.dayEl?.classList.add('selected-day');

    state.selectedDate = toLocalMidnight(info.date);
    renderEventList(containerEl, state);
}

// --- Update month/year display ---
function updateMonthYearDisplay(containerEl, date) {
    const monthNames = [
        calendarWidgetData.strings.january, calendarWidgetData.strings.february,
        calendarWidgetData.strings.march, calendarWidgetData.strings.april,
        calendarWidgetData.strings.may, calendarWidgetData.strings.june,
        calendarWidgetData.strings.july, calendarWidgetData.strings.august,
        calendarWidgetData.strings.september, calendarWidgetData.strings.october,
        calendarWidgetData.strings.november, calendarWidgetData.strings.december
    ];

    const year = date.getFullYear();
    const month = date.getMonth();

    const monthYearElement = containerEl.querySelector('.jscal-month-year');
    if (monthYearElement) {
        const monthText = monthYearElement.querySelector('.month-text');
        const yearText = monthYearElement.querySelector('.year-text');
        if (monthText) monthText.textContent = monthNames[month];
        if (yearText) yearText.textContent = year;
    }

    const pickerText = containerEl.querySelector('.jscal-picker-text');
    if (pickerText) {
        pickerText.textContent = `${monthNames[month]}, ${year}`;
    }
}

// --- Full Calendar Init ---
async function initFullCalendar(containerEl) {
    const startDay = containerEl.getAttribute('data-start-day') || 'monday';
    const showEventList = containerEl.getAttribute('data-show-event-list') === 'true';
    const calendarGridEl = containerEl.querySelector('.jscal-calendar-grid');

    if (!calendarGridEl) return;

    const isMobile = window.innerWidth <= 768;

    let calendar;
    calendar = new Calendar(calendarGridEl, {
        plugins: [dayGridPlugin, interactionPlugin],
        timeZone: 'UTC',
        initialView: 'dayGridMonth',
        firstDay: startDay === 'monday' ? 1 : 0,
        headerToolbar: false,
        height: 'auto',
        eventDisplay: 'block',
        dayMaxEventRows: isMobile ? 2 : 3,
        dayHeaderFormat: { weekday: isMobile ? 'narrow' : 'short' },
        moreLinkClick: 'popover',
        showNonCurrentDates: false,
        expandRows: true,
        eventTimeFormat: {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        },
        eventBackgroundColor: CALENDAR_CONFIG.defaultColor,
        dateClick: (info) => handleDateSelect(info, calendar, containerEl),
        eventClick: (info) => {
            info.jsEvent.preventDefault();
            const dayEl = info.el.closest('.fc-daygrid-day');
            const dateStr = info.event.startStr.slice(0, 10);
            const date = new Date(dateStr);
            handleDateSelect({ date, dateStr, dayEl }, calendar, containerEl);
            showEventPopup({
                title: info.event.title,
                start: info.event.start,
                end: info.event.end,
                allDay: info.event.allDay,
                extendedProps: info.event.extendedProps,
            });
        },
        datesSet: (info) => {
            updateMonthYearDisplay(containerEl, info.view.currentStart);
            const state = getState(containerEl);
            if (showEventList && !state.selectedDate) {
                renderEventList(containerEl, state);
            }
        },
        eventDidMount: (info) => {
            if (info.event.backgroundColor) {
                info.el.style.backgroundColor = info.event.backgroundColor;
                info.el.style.borderColor = info.event.backgroundColor;
            }
        },
    });

    // Store reference for event list access
    calendarGridEl.__fcInstance = calendar;

    calendar.render();

    // Update calendar options on resize for responsive day headers
    let resizeTimer;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            const nowMobile = window.innerWidth <= 768;
            calendar.setOption('dayHeaderFormat', { weekday: nowMobile ? 'narrow' : 'short' });
            calendar.setOption('dayMaxEventRows', nowMobile ? 2 : 3);
        }, 150);
    });

    // Setup prev/next buttons
    const prevBtn = containerEl.querySelector('.jscal-prev-month');
    const nextBtn = containerEl.querySelector('.jscal-next-month');
    if (prevBtn) prevBtn.addEventListener('click', () => calendar.prev());
    if (nextBtn) nextBtn.addEventListener('click', () => calendar.next());

    updateMonthYearDisplay(containerEl, calendar.getDate());

    // Load feeds (don't block initial paint)
    loadIcsFeeds(containerEl, calendar).catch(e => console.warn('Feed load error:', e));

    // Select today
    const today = toLocalMidnight(new Date());
    const todayStr = toLocalDateString(today);

    queueMicrotask(() => {
        const todayCell = calendarGridEl.querySelector(`.fc-day[data-date="${todayStr}"]`);
        handleDateSelect({ date: today, dateStr: todayStr, dayEl: todayCell }, calendar, containerEl);
    });
}

// --- Mini Calendar ---
class WPMiniCalendarWidget {
    constructor(container) {
        this.container = container;
        this.settings = {
            startDay: container.getAttribute('data-start-day') || 'monday',
        };
        this.currentDate = new Date();
        this.selectedDate = null;
        this.events = [];
        this.feedUrls = []; // Store feed URLs for subscription
        this.fetchIcsCache = new Map();
        this.init();
    }

    init() {
        this.setupEventListeners();
        this.loadICSFeeds();
        this.render();
    }

    setupEventListeners() {
        const prevBtn = this.container.querySelector('.jscal-prev-month');
        const nextBtn = this.container.querySelector('.jscal-next-month');
        const seeAllBtn = this.container.querySelector('.jscal-see-all-btn');

        if (prevBtn) prevBtn.addEventListener('click', () => { this.currentDate.setMonth(this.currentDate.getMonth() - 1); this.render(); });
        if (nextBtn) nextBtn.addEventListener('click', () => { this.currentDate.setMonth(this.currentDate.getMonth() + 1); this.render(); });
        if (seeAllBtn) seeAllBtn.addEventListener('click', () => {
            this.selectedDate = null;
            this.container.querySelectorAll('.day-cell').forEach(cell => cell.classList.remove('selected'));
            this.renderUpcomingEvents();
        });
    }

    async loadICSFeeds() {
        const shortcodeId = this.container.getAttribute('data-shortcode-id') || '';
        try {
            const data = await wpPost({ action: 'calendar_widget_get_ics_urls', shortcode_id: shortcodeId });
            const feeds = data?.feeds || [];

            // Store feed URLs for subscription
            this.feedUrls = feeds.filter(f => f.url?.trim()).map(f => f.url.trim());

            const results = await Promise.allSettled(
                feeds.filter(f => f.url?.trim()).map(async (f) => {
                    const icsText = await fetchIcsViaWp(this.container, f.url);
                    return parseIcsToEvents(icsText).map(e => ({
                        ...e,
                        backgroundColor: f.color || CALENDAR_CONFIG.defaultColor,
                        borderColor: f.color || CALENDAR_CONFIG.defaultColor,
                    }));
                })
            );
            for (const r of results) {
                if (r.status === 'fulfilled') this.events.push(...r.value);
            }
            this.render();
        } catch (e) {
            console.warn('Mini calendar feed load error:', e);
        }
    }

    render() {
        const year = this.currentDate.getFullYear();
        const month = this.currentDate.getMonth();
        this.updateMonthYearDisplay(year, month);
        this.renderCalendarGrid(year, month);
        this.renderUpcomingEvents();
    }

    updateMonthYearDisplay(year, month) {
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
        const monthYearElement = this.container.querySelector('.jscal-month-year');
        if (monthYearElement) {
            const monthText = monthYearElement.querySelector('.month-text');
            const yearText = monthYearElement.querySelector('.year-text');
            if (monthText) monthText.textContent = monthNames[month];
            if (yearText) yearText.textContent = year;
        }
    }

    renderCalendarGrid(year, month) {
        const grid = this.container.querySelector('.jscal-mini-grid');
        if (!grid) return;
        grid.innerHTML = '';

        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const startDayIndex = this.settings.startDay === 'monday' ? 1 : 0;

        for (let i = 0; i < 7; i++) {
            const dayIndex = (startDayIndex + i) % 7;
            const dayHeader = createElement('div', 'day-header', dayNames[dayIndex]);
            grid.appendChild(dayHeader);
        }

        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const daysInPrevMonth = new Date(year, month, 0).getDate();

        let adjustedFirstDay = firstDay - startDayIndex;
        if (adjustedFirstDay < 0) adjustedFirstDay += 7;

        for (let i = 0; i < adjustedFirstDay; i++) {
            const dayCell = createElement('div', 'day-cell empty');
            dayCell.appendChild(createElement('div', 'day-number', String(daysInPrevMonth - (adjustedFirstDay - i - 1))));
            grid.appendChild(dayCell);
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        for (let day = 1; day <= daysInMonth; day++) {
            const dayCell = createElement('div', 'day-cell');
            const date = new Date(year, month, day);
            date.setHours(0, 0, 0, 0);

            dayCell.appendChild(createElement('div', 'day-number', String(day)));

            const dayEvents = this.getEventsForDate(date);
            if (dayEvents.length > 0) dayCell.classList.add('has-events');
            if (this.selectedDate && date.toDateString() === this.selectedDate.toDateString()) {
                dayCell.classList.add('selected');
            }

            dayCell.addEventListener('click', () => {
                grid.querySelectorAll('.day-cell').forEach(cell => cell.classList.remove('selected'));
                dayCell.classList.add('selected');
                this.selectedDate = date;
                this.renderUpcomingEvents();
            });

            grid.appendChild(dayCell);
        }
    }

    getEventsForDate(date) {
        const startOfDay = new Date(date); startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(date); endOfDay.setHours(23, 59, 59, 999);
        return this.events.filter(e => new Date(e.start) >= startOfDay && new Date(e.start) <= endOfDay);
    }

    renderUpcomingEvents() {
        const eventsList = this.container.querySelector('.jscal-mini-events-list');
        const eventsTitle = this.container.querySelector('.jscal-mini-events-title');
        if (!eventsList) return;

        let eventsToShow = [];
        const today = new Date(); today.setHours(0, 0, 0, 0);

        if (this.selectedDate) {
            eventsToShow = this.getEventsForDate(this.selectedDate);
            if (eventsTitle) {
                eventsTitle.textContent = this.selectedDate.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
            }
        } else {
            eventsToShow = this.events.filter(e => new Date(e.start) >= today);
            if (eventsTitle) eventsTitle.textContent = 'Upcoming Events';
        }

        eventsToShow.sort((a, b) => new Date(a.start) - new Date(b.start));

        // Limit to 4 upcoming events when no date is selected
        if (!this.selectedDate) {
            eventsToShow = eventsToShow.slice(0, 3);
        }

        eventsList.innerHTML = '';

        if (eventsToShow.length === 0) {
            eventsList.appendChild(createElement('div', 'jscal-mini-no-events', this.selectedDate ? 'No events for this day' : 'No upcoming events'));
        } else {
            eventsToShow.forEach(event => {
                const eventItem = createElement('div', 'jscal-mini-event-item');
                const eventHeader = createElement('div', 'jscal-mini-event-header');
                eventHeader.appendChild(createElement('h3', 'jscal-mini-event-title', event.title || 'Untitled Event'));
                eventItem.appendChild(eventHeader);

                const eventMeta = createElement('div', 'jscal-mini-event-meta');
                const startDate = new Date(event.start);

                const dateItem = createElement('div', 'jscal-mini-event-meta-item');
                dateItem.innerHTML = `<i data-lucide="calendar"></i> ${startDate.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })}`;
                eventMeta.appendChild(dateItem);

                // Only show time if not an all-day event
                if (!event.allDay) {
                    eventMeta.appendChild(createElement('span', 'jscal-mini-event-meta-separator', '|'));

                    const timeItem = createElement('div', 'jscal-mini-event-meta-item');
                    timeItem.innerHTML = `<i data-lucide="clock"></i> ${startDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'UTC' })} UTC`;
                    eventMeta.appendChild(timeItem);
                }

                const extendedProps = event.extendedProps || {};
                const parser = new EventMarkdownParser(extendedProps.description);
                if (extendedProps.location) {
                    eventMeta.appendChild(createElement('span', 'jscal-mini-event-meta-separator', '|'));
                    const locationItem = createElement('div', 'jscal-mini-event-meta-item');
                    locationItem.innerHTML = `<i data-lucide="map-pin"></i> ${formatLocationValue(extendedProps.location)}`;
                    eventMeta.appendChild(locationItem);
                }

                // Organisers (from description metadata)
                const organisers = parser.getMetadata('Organisers');
                if (organisers) {
                    let organisersText = organisers.isLink ? organisers.text : (typeof organisers === 'string' ? organisers : '');
                    organisersText = organisersText.replace(/^\[|\]$/g, '');
                    if (organisersText) {
                        eventMeta.appendChild(createElement('span', 'jscal-mini-event-meta-separator', '|'));
                        const organisersItem = createElement('div', 'jscal-mini-event-meta-item');
                        organisersItem.innerHTML = `<i data-lucide="users"></i> ${escapeHtml(organisersText)}`;
                        eventMeta.appendChild(organisersItem);
                    }
                }

                // Meeting URL (from description metadata)
                const meetingUrl = parser.getMetadata('Meeting URL');
                if (meetingUrl) {
                    const meetingUrlValue = meetingUrl.isLink ? meetingUrl.url : (typeof meetingUrl === 'string' ? meetingUrl : '');
                    if (meetingUrlValue) {
                        eventMeta.appendChild(createElement('span', 'jscal-mini-event-meta-separator', '|'));
                        const meetingItem = createElement('div', 'jscal-mini-event-meta-item');
                        meetingItem.innerHTML = `<i data-lucide="link"></i> <a href="${escapeHtml(meetingUrlValue)}" target="_blank" rel="noopener" class="jscal-mini-meeting-link">Meeting Link</a>`;
                        eventMeta.appendChild(meetingItem);
                    }
                }

                eventItem.appendChild(eventMeta);
                eventsList.appendChild(eventItem);
            });
        }

        createIcons({ icons: lucideIcons });
    }
}

// --- Init all calendars ---
async function initCalendars() {
    // Full calendars
    const containers = document.querySelectorAll('.jscal-container');
    for (const containerEl of containers) {
        initFullCalendar(containerEl);
    }

    // Mini calendars
    const miniCalendars = document.querySelectorAll('.jscal-mini-wrapper');
    miniCalendars.forEach(container => {
        const calendar = new WPMiniCalendarWidget(container);
        container.__wpMiniCalendarInstance = calendar;
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCalendars);
} else {
    initCalendars();
}
